import { useCallback, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { message } from "antd";

export interface SpeechSegment {
  text: string;
  key: string | null;
}

export type SpeechState = "idle" | "playing" | "paused";

const TTS_LOCALE_MAP: Record<string, string> = {
  zh: "zh-CN",
  "zh-CN": "zh-CN",
  "zh-SG": "zh-CN",
  "zh-TW": "zh-TW",
  "zh-HK": "zh-TW",
  "zh-MO": "zh-TW",
  en: "en-US",
  "en-AU": "en-AU",
  "en-CA": "en-CA",
  "en-GB": "en-GB",
  ar: "ar-SA",
  cs: "cs-CZ",
  da: "da-DK",
  de: "de-DE",
  el: "el-GR",
  es: "es-ES",
  fa: "fa-IR",
  fi: "fi-FI",
  fr: "fr-FR",
  ga: "ga-IE",
  gl: "gl-ES",
  gu: "gu-IN",
  he: "he-IL",
  hi: "hi-IN",
  hu: "hu-HU",
  id: "id-ID",
  it: "it-IT",
  ja: "ja-JP",
  ko: "ko-KR",
  lb: "lb-LU",
  lt: "lt-LT",
  lv: "lv-LV",
  my: "my-MM",
  ne: "ne-NP",
  nl: "nl-NL",
  no: "nb-NO",
  pl: "pl-PL",
  pt: "pt-PT",
  "pt-BR": "pt-BR",
  ro: "ro-RO",
  ru: "ru-RU",
  sk: "sk-SK",
  sl: "sl-SI",
  sq: "sq-AL",
  sr: "sr-RS",
  sv: "sv-SE",
  ta: "ta-IN",
  th: "th-TH",
  tr: "tr-TR",
  uk: "uk-UA",
  vi: "vi-VN",
};

const resolveUtteranceLang = (locale: string): string =>
  TTS_LOCALE_MAP[locale] || TTS_LOCALE_MAP[locale.split("-")[0]] || "en-US";

export function useSpeech(segments: SpeechSegment[], locale: string) {
  const intl = useIntl();
  const supported =
    typeof window !== "undefined" &&
    "speechSynthesis" in window &&
    "SpeechSynthesisUtterance" in window;
  const [state, setState] = useState<SpeechState>("idle");
  const [currentKey, setCurrentKey] = useState<string | null>(null);
  const requestRef = useRef(0);
  const indexRef = useRef(0);
  const [voicesReady, setVoicesReady] = useState(false);
  const [voiceSupported, setVoiceSupported] = useState(false);
  const [voicesEmpty, setVoicesEmpty] = useState(true);
  const lang = resolveUtteranceLang(locale);

  const stop = useCallback(() => {
    requestRef.current += 1;
    indexRef.current = 0;
    setState("idle");
    setCurrentKey(null);
    if (supported) {
      try {
        const synth = window.speechSynthesis;
        synth.pause();
        synth.cancel();
      } catch {}
    }
  }, [supported]);

  // Stop whenever the content or language changes.
  useEffect(() => {
    stop();
  }, [segments, locale, stop]);

  // Cancel any playback on unmount.
  useEffect(
    () => () => {
      if (supported) {
        try {
          window.speechSynthesis.cancel();
        } catch {}
      }
    },
    [supported]
  );

  // 语音列表是异步加载的（部分浏览器需等 voiceschanged），据此判断目标语言是否有可用 voice
  useEffect(() => {
    if (!supported) {
      setVoicesReady(true);
      return;
    }
    let changed = false;
    const update = () => {
      try {
        const voices = window.speechSynthesis.getVoices();
        const base = lang.split("-")[0].toLowerCase();
        const ok = voices.some((v) => {
          const vl = (v.lang || "").toLowerCase();
          return vl === lang.toLowerCase() || vl.split("-")[0] === base;
        });
        setVoiceSupported(ok);
        setVoicesEmpty(voices.length === 0);
        setVoicesReady(voices.length > 0 || changed);
      } catch {}
    };
    const onVoicesChanged = () => {
      changed = true;
      update();
    };
    update();
    try {
      window.speechSynthesis.addEventListener?.("voiceschanged", onVoicesChanged);
    } catch {}
    // 个别浏览器始终不触发 voiceschanged，兜底标记已检测完成
    const fallback = window.setTimeout(() => {
      if (!changed) {
        changed = true;
        update();
      }
    }, 2000);
    return () => {
      window.clearTimeout(fallback);
      try {
        window.speechSynthesis.removeEventListener?.("voiceschanged", onVoicesChanged);
      } catch {}
    };
  }, [supported, lang]);

  const speak = useCallback(
    (startIndex: number) => {
      if (!supported || segments.length === 0) return;
      const requestId = requestRef.current + 1;
      requestRef.current = requestId;
      indexRef.current = startIndex;
      const synth = window.speechSynthesis;
      synth.cancel();
      // 显式选中与目标语言匹配的 voice，避免浏览器用默认音色或静默失败
      let matched: SpeechSynthesisVoice | null = null;
      try {
        const base = lang.split("-")[0].toLowerCase();
        const voices = synth.getVoices();
        matched =
          voices.find((v) => (v.lang || "").toLowerCase() === lang.toLowerCase()) ||
          voices.find((v) => (v.lang || "").toLowerCase().split("-")[0] === base) ||
          null;
      } catch {}

      const speakNext = () => {
        if (requestRef.current !== requestId) return;
        const segment = segments[indexRef.current];
        if (!segment) {
          setState("idle");
          setCurrentKey(null);
          return;
        }
        setCurrentKey(segment.key);
        const utterance = new SpeechSynthesisUtterance(segment.text);
        utterance.lang = lang;
        if (matched) utterance.voice = matched;
        utterance.onend = () => {
          if (requestRef.current !== requestId) return;
          indexRef.current += 1;
          speakNext();
        };
        utterance.onerror = (e) => {
          if (requestRef.current !== requestId) return;
          if (e.error === "interrupted" || e.error === "canceled") {
            indexRef.current += 1;
            speakNext();
            return;
          }
          setState("idle");
          setCurrentKey(null);
          message.warning(intl.formatMessage({ id: "settings.voicePlaybackFailed" }, { lang }));
        };
        synth.speak(utterance);
      };

      setState("playing");
      // iOS Safari 要求 speak() 在用户手势的同步调用栈内执行，
      // 用 setTimeout 延迟会导致静默失败（点了没声音），所以直接同步开始。
      speakNext();
    },
    [supported, segments, locale, lang, intl]
  );

  const toggle = useCallback(() => {
    if (!supported || segments.length === 0) return;
    if (state === "playing") {
      requestRef.current += 1;
      setState("paused");
      setCurrentKey(null);
      try {
        const synth = window.speechSynthesis;
        synth.pause();
        synth.cancel();
      } catch {}
      return;
    }
    if (state === "paused") {
      speak(indexRef.current);
      return;
    }
    // idle：先确认当前语言有可用语音。列表明确存在但无匹配 → 提示；
    // 列表为空（部分浏览器不暴露语音列表）→ 仅当目标语言与设备语言一致才尝试，
    // 否则提示，避免用默认语音乱读（如用中文语音读日语）
    if (!voiceSupported) {
      // iOS Safari 首次用户交互前 getVoices() 为空，借手势时机重新读取一次再判断
      let freshOk = false;
      try {
        const base = lang.split("-")[0].toLowerCase();
        const voices = window.speechSynthesis.getVoices();
        freshOk = voices.some((v) => {
          const vl = (v.lang || "").toLowerCase();
          return vl === lang.toLowerCase() || vl.split("-")[0] === base;
        });
      } catch {}
      if (freshOk) {
        speak(0);
        return;
      }
      const deviceBase = (
        typeof navigator !== "undefined" && navigator.language ? navigator.language.split("-")[0] : "en"
      ).toLowerCase();
      const targetBase = lang.split("-")[0].toLowerCase();
      // 空列表时：设备语言或英文通常系统自带语音，允许尝试；其余语言提示，避免乱读
      if (voicesEmpty && (deviceBase === targetBase || targetBase === "en")) {
        speak(0);
        return;
      }
      if (!voicesReady) {
        message.info(intl.formatMessage({ id: "settings.voiceChecking" }));
      } else {
        message.warning(intl.formatMessage({ id: "settings.voiceNotSupported" }, { lang }));
      }
      return;
    }
    speak(0);
  }, [supported, segments.length, state, speak, voiceSupported, voicesEmpty, voicesReady, intl, lang]);

  return {
    supported,
    voiceSupported,
    voicesReady,
    lang,
    state,
    currentKey,
    canPlay: segments.length > 0,
    toggle,
    stop,
  };
}
